// ============================================================================
// TEMPLATE GENERAL INFO PANE (FSA-1)
// ============================================================================
// Replaces the old always-visible metadata strip. Shows template
// header fields as read-only key/value cards laid out in a grid.

import React from 'react';
import type { FsTemplate } from 'api/types/fsa/template.types';

interface TemplateGeneralInfoProps {
  template: FsTemplate;
  reportCount: number;
  scheduleCount: number;
  noteCount: number;
}

const formatDate = (iso?: string | null): string => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
};

const TemplateGeneralInfo: React.FC<TemplateGeneralInfoProps> = ({
  template,
  reportCount,
  scheduleCount,
  noteCount,
}) => {
  return (
    <div style={{ padding: '0.5rem 0' }}>
      <h3 style={titleStyle}>Template Overview</h3>
      <p style={subtitleStyle}>
        Read-only summary of the template as defined by the system. This template will pre-fill
        engagement-level FSA settings (currency, rounding factor) when applied to a basic-tier
        engagement.
      </p>

      {/* ---- Section 1: Identity ---- */}
      <SectionTitle>Identity</SectionTitle>
      <Grid>
        <Field label="Code" value={template.code} mono />
        <Field label="Template Name" value={template.name} />
        <Field
          label="Origin"
          value={
            <span style={template.isSystem ? originSystemStyle : originFirmStyle}>
              {template.isSystem ? 'System (Seeded)' : 'Firm (Custom)'}
            </span>
          }
        />
        <Field
          label="Status"
          value={
            <span className={`badge badge-${template.statusCode === 'ACTIVE' ? 'active' : 'inactive'}`}>
              {template.statusName ?? template.statusCode}
            </span>
          }
        />
      </Grid>

      {template.description && (
        <div style={{ marginTop: '0.5rem' }}>
          <Field label="Description" value={template.description} fullWidth />
        </div>
      )}

      {/* ---- Section 2: Applicability ---- */}
      <SectionTitle>Applicability</SectionTitle>
      <Grid>
        <Field label="Entity Type" value={prettyEntityType(template.entityType)} />
        <Field label="Jurisdiction" value={template.jurisdictionCode} />
        <Field label="Statutory Basis" value={prettyStatutoryBasis(template.statutoryBasis)} />
        <Field label="Effective From" value={formatDate(template.applicableFrom)} />
        <Field label="Effective To" value={formatDate(template.applicableTo)} />
      </Grid>

      {/* ---- Section 3: Defaults ---- */}
      <SectionTitle>Defaults Applied to Engagement</SectionTitle>
      <Grid>
        <Field
          label="Currency"
          value={
            template.defaultCurrencyCode
              ? `${template.defaultCurrencyCode}${template.defaultCurrencySymbol ? ` (${template.defaultCurrencySymbol})` : ''}${template.defaultCurrencyName ? ` — ${template.defaultCurrencyName}` : ''}`
              : '—'
          }
        />
        <Field
          label="Rounding Factor"
          value={template.defaultRoundingFactorLabel ?? '—'}
          hint={template.defaultRoundingFactorLabel ? 'Numbers are reported in this rounding factor by default.' : undefined}
        />
      </Grid>

      {/* ---- Section 4: Contents at a glance ---- */}
      <SectionTitle>Contents at a Glance</SectionTitle>
      <Grid>
        <Field label="Reports" value={String(reportCount)} hint="Deliverables in the bundle (BS, P&L, Notes, etc.)" />
        <Field label="Schedules" value={String(scheduleCount)} hint="Sub-sections across all reports" />
        <Field label="Notes" value={String(noteCount)} hint="Numbered notes attached to BS/P&L line items" />
      </Grid>

      {/* ---- Section 5: Audit ---- */}
      <SectionTitle>Audit</SectionTitle>
      <Grid>
        <Field label="Created On" value={formatDate(template.createdAt)} />
        <Field label="Created By" value={template.createdByName ?? '—'} />
        <Field label="Updated On" value={formatDate(template.updatedAt)} />
        <Field label="Updated By" value={template.updatedByName ?? '—'} />
      </Grid>
    </div>
  );
};

export default TemplateGeneralInfo;

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h4 style={sectionTitleStyle}>{children}</h4>
);

const Grid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={gridStyle}>{children}</div>
);

const Field: React.FC<{
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  fullWidth?: boolean;
  hint?: string;
}> = ({ label, value, mono, fullWidth, hint }) => (
  <div style={{ ...fieldStyle, ...(fullWidth ? { gridColumn: '1 / -1' } : {}) }}>
    <div style={fieldLabelStyle}>{label}</div>
    <div style={{ ...fieldValueStyle, ...(mono ? { fontFamily: 'monospace' } : {}) }}>
      {value || '—'}
    </div>
    {hint && <div style={fieldHintStyle}>{hint}</div>}
  </div>
);

// ============================================================================
// HELPERS
// ============================================================================

function prettyEntityType(et: string): string {
  switch (et) {
    case 'COMPANY':         return 'Company';
    case 'LLP':             return 'Limited Liability Partnership (LLP)';
    case 'PARTNERSHIP':     return 'Partnership Firm';
    case 'SOLE_PROPRIETOR': return 'Sole Proprietor';
    case 'TRUST':           return 'Trust';
    default:                return et;
  }
}

function prettyStatutoryBasis(sb: string): string {
  switch (sb) {
    case 'SCHEDULE_III_DIV_I':  return 'Schedule III Division I (Companies Act 2013)';
    case 'SCHEDULE_III_DIV_II': return 'Schedule III Division II (Ind AS)';
    case 'SCHEDULE_III_DIV_III':return 'Schedule III Division III (NBFC)';
    case 'ICAI_NCE_PARTNERSHIP':    return 'ICAI Guidance Note on Financial Statements of Non-Corporate Entities (Partnership Firm)';
    case 'ICAI_NCE_PROPRIETORSHIP': return 'ICAI Guidance Note on Financial Statements of Non-Corporate Entities (Sole Proprietorship)';
    default:                    return sb;
  }
}

// ============================================================================
// STYLES
// ============================================================================

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '1.05rem',
  fontWeight: 700,
  color: 'var(--text-primary)',
};

const subtitleStyle: React.CSSProperties = {
  margin: '0.25rem 0 1rem 0',
  fontSize: '0.8125rem',
  color: 'var(--text-secondary)',
  lineHeight: 1.5,
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: '0.7rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.6,
  color: 'var(--text-secondary)',
  margin: '1.25rem 0 0.5rem 0',
  paddingBottom: '0.25rem',
  borderBottom: '1px solid var(--border-color)',
};

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: '0.75rem 1.25rem',
};

const fieldStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

const fieldLabelStyle: React.CSSProperties = {
  fontSize: '0.7rem',
  fontWeight: 600,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};

const fieldValueStyle: React.CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--text-primary)',
  fontWeight: 500,
};

const fieldHintStyle: React.CSSProperties = {
  fontSize: '0.7rem',
  color: 'var(--text-secondary)',
  fontStyle: 'italic',
};

const originSystemStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  fontWeight: 600,
  padding: '2px 8px',
  borderRadius: 999,
  background: '#dbeafe',
  color: '#1e3a8a',
};

const originFirmStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  fontWeight: 600,
  padding: '2px 8px',
  borderRadius: 999,
  background: '#dcfce7',
  color: '#166534',
};
